import _ from 'lodash'
import { SagaReturnType, call, put, select, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

import { AuthUser } from '@/core/classes/Auth'
import staffModel, { Availability, CalendsoEventType, ContactDto, CreateStaffDto, Staff, StaffAvailability } from '@/core/models/staff.model'
import { SearchRequestOptions, SearchStaffsAvailabilityRequestParams } from '@/core/types/request'

import { RootState } from '@/redux/rootReducer'
import { selectAuthUser, setAuthUser as setAuthUserAction, updateMyProfileSuccess as updateMyProfileSuccessAction } from '@/redux/reducers/auth.reducer'
import {
  getStaff as getStaffAction,
  getStaffSucess as getStaffSucessAction,
  getStaffFailure as getStaffFailureAction,
  getStaffs as getStaffsAction,
  getStaffsSuccess as getStaffsSuccessAction,
  getStaffsFailure as getStaffsFailureAction,
  getStaffAvailability as getStaffAvailabilityAction,
  getStaffAvailabilitySuccess as getStaffAvailabilitySuccessAction,
  getStaffAvailabilityFailure as getStaffAvailabilityFailureAction,
  getStaffsAvailability as getStaffsAvailabilityAction,
  getStaffsAvailabilitySuccess as getStaffsAvailabilitySuccessAction,
  getStaffsAvailabilityFailure as getStaffsAvailabilityFailureAction,
  getStaffsEvents as getStaffsEventsAction,
  getStaffsEventsSuccess as getStaffsEventsSuccessAction,
  getStaffsEventsFailure as getStaffsEventsFailureAction,
  handleContact as handleContactAction,
  handleContactSuccess as handleContactSuccessAction,
  handleContactFailure as handleContactFailureAction,
  createDirectMsg as createDirectMsgAction,
  createDirectMsgSuccess as createDirectMsgSuccessAction,
  createDirectMsgFailure as createDirectMsgFailureAction,
  archiveRoom as archiveRoomAction,
  archiveRoomSuccess as archiveRoomSuccessAction,
  archiveRoomFailure as archiveRoomFailureAction,
  addStaff as addStaffAction,
  addStaffSuccess as addStaffSuccessAction,
  addStaffFailure as addStaffFailureAction,
  configureStripePayment as configureStripePaymentAction,
  configureStripePaymentSuccess as configureStripePaymentSuccessAction,
  configureStripePaymentFailure as configureStripePaymentFailureAction,
  deleteStripeAccount as deleteStripeAccountAction,
  deleteStripeAccountSuccess as deleteStripeAccountSuccessAction,
  deleteStripeAccountFailure as deleteStripeAccountFailureAction,
  selectStaffById,
} from '@/redux/reducers/staff.reducer'

import { handleError } from '@/utils/errors'

function* getStaff(action: PayloadAction<string>) {
  try {
    const staffId = action.payload
    const staff: Staff = yield call(staffModel.getStaff, staffId)
    yield put(getStaffSucessAction(staff))
  } catch (exception) {
    console.warn(exception)
    yield put(getStaffFailureAction(handleError(exception)))
  }
}

function* getStaffs(
  action: PayloadAction<{
    query: Record<string, any>
    options?: SearchRequestOptions
  }>
) {
  try {
    const { query, options } = action.payload
    const [staffs, totalCount]: [Staff[], number] = yield call(staffModel.getStaffs, query, options)
    yield put(getStaffsSuccessAction({ items: staffs, totalCount }))
  } catch (exception) {
    console.warn(exception)
    yield put(getStaffsFailureAction(handleError(exception)))
  }
}

function* getStaffAvailability(
  action: PayloadAction<{
    username: string
    dateFrom: string
    dateTo: string
    staffId: string
  }>
) {
  try {
    const { username, dateFrom, dateTo, staffId } = action.payload
    // const [staffId,slots]: [string,Slot[]] = yield call(staffModel.getStaffAvailability, calendso, duration, offset)
    const availability: Availability = yield call(staffModel.getStaffAvailability, username, dateFrom, dateTo, staffId)
    yield put(getStaffAvailabilitySuccessAction({ availability, staffId }))
  } catch (exception) {
    console.warn(exception)
    yield put(getStaffAvailabilityFailureAction(handleError(exception)))
  }
}

function* getStaffsAvailability(action: PayloadAction<SearchStaffsAvailabilityRequestParams>) {
  try {
    const staffsAvalability: StaffAvailability[] = yield call(staffModel.searchStaffsAvailability, action.payload)
    yield put(getStaffsAvailabilitySuccessAction(staffsAvalability))
  } catch (exception) {
    console.warn(exception)
    yield put(getStaffsAvailabilityFailureAction())
  }
}

function* getStaffsEvents(action: PayloadAction<{ staffId: string }[]>) {
  try {
    const events: { events: CalendsoEventType[]; id: string }[] = yield call(staffModel.searchStaffsEvents, action.payload)
    yield put(getStaffsEventsSuccessAction(events))
  } catch (exception) {
    console.warn(exception)
    yield put(getStaffsEventsFailureAction())
  }
}

function* handleContact(action: PayloadAction<ContactDto>) {
  try {
    const staff: Staff = yield call(staffModel.handleContact, action.payload)
    yield put(handleContactSuccessAction({ staff }))
    const authUser: AuthUser | null = yield select(selectAuthUser)
    yield put(setAuthUserAction(authUser ? { ...authUser, contacts: staff.contacts } : staff))
  } catch (exception) {
    console.warn(exception)
    yield put(handleContactFailureAction(handleError(exception)))
  }
}

function* createDirectMsg(action: PayloadAction<{ email: string }>) {
  try {
    const { email } = action.payload
    yield put(createDirectMsgSuccessAction(yield call(staffModel.createDirectMsg, email)))
  } catch (exception) {
    yield put(createDirectMsgFailureAction(handleError(exception)))
  }
}

function* archiveRoom(action: PayloadAction<{ roomId: string }>) {
  try {
    const { roomId } = action.payload
    yield put(archiveRoomSuccessAction(yield call(staffModel.createArchiveRoom, roomId)))
  } catch (exception) {
    yield put(archiveRoomFailureAction(handleError(exception)))
  }
}

function* addStaff(action: PayloadAction<CreateStaffDto>) {
  try {
    const staff: Staff = yield call(staffModel.addStaff, action.payload)
    yield put(addStaffSuccessAction(staff))
  } catch (exception) {
    console.warn(exception)
    yield put(addStaffFailureAction(handleError(exception)))
  }
}

function* configureStripePayment(action: PayloadAction<{ staffId: string; refreshUrl: string; returnUrl: string }>) {
  try {
    const { staffId, refreshUrl, returnUrl } = action.payload
    const existingStaff: Staff | undefined = yield select((state: RootState) => selectStaffById(state, staffId))
    if (!existingStaff) {
      const staff: Staff = yield call(staffModel.getStaff, staffId)
      if (!staff.stripe) yield call(staffModel.createStripeAccount, staffId)
    } else if (!!existingStaff && !existingStaff.stripe) {
      yield call(staffModel.createStripeAccount, staffId)
    }
    const url: string = yield call(staffModel.getAccountLink, { refresh_url: refreshUrl, return_url: returnUrl })
    yield put(configureStripePaymentSuccessAction())
    window.location.assign(url)
  } catch (exception) {
    yield put(configureStripePaymentFailureAction(handleError(exception)))
  }
}

function* deleteStripePayment(action: PayloadAction<ReturnType<typeof deleteStripeAccountAction>['payload']>) {
  try {
    const staff: SagaReturnType<typeof staffModel.deleteStripeAccount> = yield call(staffModel.deleteStripeAccount, action.payload.staffId)
    const authUser: ReturnType<typeof selectAuthUser> = yield select(selectAuthUser)
    yield put(updateMyProfileSuccessAction(_.merge({}, authUser, staff)))
    yield put(deleteStripeAccountSuccessAction())
  } catch (exception) {
    yield put(deleteStripeAccountFailureAction(handleError(exception)))
  }
}



const sagas = [
  takeLatest(getStaffAction.type, getStaff),
  takeLatest(getStaffsAction.type, getStaffs),
  takeLatest(getStaffAvailabilityAction.type, getStaffAvailability),
  takeLatest(getStaffsAvailabilityAction.type, getStaffsAvailability),
  takeLatest(getStaffsEventsAction.type, getStaffsEvents),
  takeLatest(handleContactAction.type, handleContact),
  takeLatest(createDirectMsgAction.type, createDirectMsg),
  takeLatest(archiveRoomAction.type, archiveRoom),
  takeLatest(addStaffAction.type, addStaff),
  takeLatest(configureStripePaymentAction.type, configureStripePayment),
  takeLatest(deleteStripeAccountAction.type, deleteStripePayment),
]

export default sagas
